/**
 * useSocialFeed - React Query hook for social activity feed
 *
 * Phase 5F: Replaces SocialPanel's local state with React Query
 *
 * Features:
 * - useInfiniteQuery for cursor-paginated feed
 * - Cache-first with background revalidation
 * - Bounded persistence for first page only (cursor=null, limit<=20)
 * - Realtime subscription to feed_items INSERT events
 */

import { useCallback, useEffect } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import { supabase } from '../lib/supabase';
import type { Activity, FeedResponse, FeedFilter } from '../types/activity';

// ============================================================================
// Types
// ============================================================================

interface UseSocialFeedResult {
  activities: Activity[];
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
}

interface UseSocialFeedOptions {
  filter?: FeedFilter;
  limit?: number;
  enabled?: boolean;
}

// ============================================================================
// Hook
// ============================================================================

export function useSocialFeed(options: UseSocialFeedOptions = {}): UseSocialFeedResult {
  const { filter = 'all', limit = 20, enabled = true } = options;
  const { session, user } = useAuth();
  const queryClient = useQueryClient();

  const userId = user?.id;
  const token = session?.access_token;

  // Query key for this feed
  const queryKey = userId
    ? queryKeys.social.feed(userId, { filter, cursor: null, limit })
    : ['social', 'anonymous'];

  // Infinite query for feed
  const {
    data,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }): Promise<FeedResponse> => {
      if (!token) {
        throw new Error('Authentication required');
      }

      const params = new URLSearchParams({
        limit: String(limit),
        filter,
      });
      if (pageParam) {
        params.set('cursor', pageParam);
      }

      return apiFetch<FeedResponse>(`/feed?${params.toString()}`, { token });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: enabled && !!userId && !!token,
    staleTime: 60_000, // 60 seconds
    gcTime: 30 * 60_000, // 30 minutes
  });

  // Flatten pages into single array
  const activities = data?.pages.flatMap((page) => page.activities) ?? [];
  const hasMore = !!hasNextPage;

  // Load more callback
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetching && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetching, isFetchingNextPage, fetchNextPage]);

  // Realtime subscription to feed_items INSERT events
  // Uses shared supabase client (authenticated with current session)
  useEffect(() => {
    if (!userId || !enabled) return;

    // Subscribe to INSERT events on feed_items for this user
    const channel = supabase
      .channel(`feed_items:user_id=eq.${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'feed_items',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          // On new feed item, invalidate the feed query to trigger refetch
          // This is a targeted invalidation (only this user's feed)
          queryClient.invalidateQueries({
            queryKey: queryKeys.social.feedBase(userId),
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, enabled, queryClient]);

  return {
    activities,
    isLoading,
    isFetching,
    isFetchingNextPage,
    error: error ? 'Failed to load feed' : null,
    hasMore,
    loadMore,
  };
}
